/**
 * Single-user filter: open it for a dropdown of suggested users, or type
 * free-form (email or name) to run a backend search; pick one to filter by.
 * Clearing the pick restores the unfiltered view.
 *
 * The sibling of QuestionSearchFilter (see ../question-picker) for the user
 * dimension. Search is server-side (`/console/users?search=`) so it keeps
 * working once a consumer paginates and no longer holds every row on the
 * client. `suggestions` are shown before anything is typed — the users the
 * consumer already knows are relevant (e.g. those on the current page) — so the
 * common pick costs no request.
 *
 * The control is keyed on email by default (the identity operations lists have
 * always filtered by), but `valueKey="userId"` switches it to the user id.
 * Prefer that: a school roster account has NO email — it is provisioned with a
 * loginId and nothing else — so an email-keyed pick emits '', which is the
 * same thing clearing emits, and the filter silently does nothing.
 *
 * `/console/users` is admin-gated, so this control is only meaningful under an
 * admin token (the admin console's ApiProvider).
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api';
import './UserSearchFilter.css';

export interface UserSearchOption {
  userId: string;
  email: string;
  /** Display name, shown alongside the email. */
  name?: string;
}

export interface UserSearchFilterProps {
  /** Users offered before anything is typed (no request). */
  suggestions?: UserSearchOption[];
  /** Heading above the suggestions list. */
  suggestionsLabel?: string;
  /** Which field `value`/`onChange` carry. Defaults to 'email' for the callers
   *  that predate this; 'userId' is correct for anything that can meet a user
   *  without an email. */
  valueKey?: 'email' | 'userId';
  /** Currently filtered-to user, keyed per `valueKey`, or '' for no filter. */
  value: string;
  onChange: (value: string) => void;
  /** The whole chosen option (null on clear), for consumers that need the
   *  userId. `onChange` only carries the email, so resolving back to a user
   *  meant a second fuzzy search that could land on a different person. */
  onPick?: (option: UserSearchOption | null) => void;
  /** Drop anonymous pre-signup guests from the results. For pickers that choose
   *  someone to act ON — preview as, target, impersonate — where a guest is
   *  never a valid answer. (Seeded dummies and persona agents are already
   *  excluded by the endpoint's defaults.) */
  excludeGuests?: boolean;
  /** Text for the chip when the chosen email isn't among `suggestions` and
   *  hasn't been searched up (e.g. restored from a URL). */
  valueText?: string;
  placeholder?: string;
  /** Minimum query length before a request fires (default 2). */
  minChars?: number;
  /** Narrow the search to one tenant. The server honours it only for callers
   *  who may see across tenants and ignores it for everyone else, so passing
   *  it is never a widening. Absent = whatever the caller may see. */
  tenantId?: string;
}

interface UserListItem {
  id: string;
  name?: string;
  email: string;
}

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 350;

/** A name if there is one, else an email, else the id — never blank, because a
 *  roster account has only the first of those. */
const optionLabel = (o: UserSearchOption): string => o.name?.trim() || o.email || o.userId;

export function UserSearchFilter({
  suggestions = [],
  suggestionsLabel = 'Suggested',
  valueKey = 'email',
  value,
  onChange,
  onPick,
  valueText,
  placeholder = 'Search users…',
  minChars = 2,
  excludeGuests = false,
  tenantId,
}: UserSearchFilterProps) {
  const { fetchApi } = useApi();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  // Remembered so the chip can show the label of a user picked from search
  // results, which the consumer's suggestions may not contain.
  const [pickedLabel, setPickedLabel] = useState<string | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const searching = debounced.length >= minChars;

  const { data, isFetching } = useQuery({
    queryKey: ['user-search', debounced, excludeGuests, tenantId],
    queryFn: () => {
      const params = new URLSearchParams({ search: debounced, pageSize: String(PAGE_SIZE) });
      if (excludeGuests) params.set('excludeGuests', 'true');
      if (tenantId) params.set('tenantId', tenantId);
      return fetchApi<{ items: UserListItem[]; total: number }>(`/console/users?${params}`);
    },
    enabled: open && searching,
    staleTime: 60_000,
  });

  const results: UserSearchOption[] = useMemo(() => {
    if (!searching) return suggestions;
    return (data?.items ?? []).map(u => ({ userId: u.id, email: u.email, name: u.name }));
  }, [searching, suggestions, data]);

  const keyOf = (o: UserSearchOption): string => (valueKey === 'userId' ? o.userId : o.email);

  const selectedLabel = useMemo(() => {
    if (!value) return undefined;
    return pickedLabel ?? suggestions.find(o => keyOf(o) === value)?.name ?? valueText ?? value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, pickedLabel, suggestions, valueText, valueKey]);

  const pick = (o: UserSearchOption) => {
    setPickedLabel(optionLabel(o));
    onChange(keyOf(o));
    onPick?.(o);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    setPickedLabel(undefined);
    onChange('');
    onPick?.(null);
    setQuery('');
  };

  // A chosen user replaces the input with a removable chip.
  if (value) {
    return (
      <div className="usf" ref={ref}>
        <span className="usf-chip" title={selectedLabel === value ? value : `${selectedLabel} · ${value}`}>
          <span className="usf-chip-text">{selectedLabel}</span>
          <button type="button" className="usf-chip-clear" onClick={clear} aria-label="Clear user filter">
            &times;
          </button>
        </span>
      </div>
    );
  }

  const headLabel = isFetching
    ? 'Searching…'
    : searching
      ? (results.length === 0
        ? 'No matching users'
        : `${data?.total ?? results.length} match${(data?.total ?? results.length) === 1 ? '' : 'es'}${(data?.total ?? 0) > results.length ? ` — showing ${results.length}` : ''}`)
      : (results.length === 0
        ? `Type at least ${minChars} characters to search`
        : suggestionsLabel);

  return (
    <div className="usf" ref={ref}>
      <input
        type="text"
        className="usf-input"
        placeholder={placeholder}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && results.length === 1) pick(results[0]);
        }}
        aria-label="Search users"
      />
      <span className="usf-caret" aria-hidden="true">▾</span>
      {open && (
        <div className="usf-popup" role="listbox">
          <div className="usf-popup-head">{headLabel}</div>
          {results.map(o => (
            <button
              key={o.userId}
              type="button"
              className="usf-result"
              role="option"
              // Suppress the mousedown default so the pick cannot be stolen:
              // it keeps focus in the search input (standard combobox
              // behaviour) and, more importantly, stops WebKit forwarding the
              // click to a surrounding <label>'s control — which silently
              // swallowed every selection when a consumer wrapped this widget
              // in a label.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              <span className="usf-result-text">{optionLabel(o)}</span>
              {o.name && <span className="usf-result-hint">{o.email}</span>}
            </button>
          ))}
          {!searching && query.length > 0 && (
            <div className="usf-popup-foot">Keep typing to search all users…</div>
          )}
        </div>
      )}
    </div>
  );
}
